/**
 * A MusicXML file read into a score.
 *
 * Failure is a value here, as it is everywhere else in this package: a file that is not
 * XML, or is XML but not a score, comes back as a reason rather than an exception, because
 * the caller is usually a drop target or a file picker and has to say something useful
 * either way.
 *
 * The global timeline is rebuilt from the parts rather than taken from one of them. Every
 * part repeats the metre and key, and a file may state a tempo in one part and not in
 * another, so each bar takes the first statement any part makes of each -- which is also
 * what makes a file this package exported, where only the first part carries the tempo,
 * come back unchanged.
 */

import { createId } from "../document/ids";
import type { MeasureId, PartId } from "../document/ids";
import { AT_PITCH } from "../document/pitch";
import type { Transposition } from "../document/pitch";
import { measureTicks } from "../document/duration";
import { C_MAJOR, COMMON_TIME, TREBLE } from "../document/types";
import type {
  GlobalMeasure,
  JumpMarks,
  Part,
  PartMeasure,
  RepeatMarks,
  Score,
  Spanner,
  Staff,
  StaffGroup,
} from "../document/types";
import { emptyPartMeasure } from "../document/create";
import { parseXml } from "../xml/parse";
import type { XmlElement } from "../xml/parse";
import { attr, childrenNamed } from "../xml/read";
import { barlineStyleOf } from "./common";
import { groupOf, readLayout, readMeta, readPartList } from "./import-header";
import type { PartEntry } from "./import-header";
import { kitFrom } from "./import-drums";
import { simplifyAccidentals } from "./import-accidentals";
import { keyPrintingOf, pieceKey } from "./import-key";
import type { KeyStatement } from "./import-key";
import { readMeasure } from "./import-measure";
import type { MeasureRead, PartState } from "./import-bar";
import { isPlain, ossiaMap, remapMeasure } from "./import-ossia";
import type { OssiaMap } from "./import-ossia";

/** The outcome of reading a file. */
export type ImportResult =
  | { readonly ok: true; readonly score: Score; readonly warnings: readonly string[] }
  | { readonly ok: false; readonly reason: string };

interface PartRead {
  readonly reads: readonly MeasureRead[];
  readonly state: PartState;
}

function readPart(element: XmlElement | null, entry: PartEntry): PartRead {
  const state: PartState = {
    divisions: 1,
    staffCount: 1,
    transposition: AT_PITCH,
    open: new Map(),
    closed: [],
    last: null,
    roster: entry.roster,
    opening: {
      name: entry.instrument.name,
      sound: entry.instrument.sound,
      midiProgram: entry.instrument.midiProgram,
    },
    playing: null,
    opened: null,
    drums: entry.drums,
    seen: new Map(),
    staffLines: new Map(),
    ossiaStaves: new Set(),
    tuning: null,
    gathered: false,
  };
  const reads = childrenNamed(element, "measure").map((measure) => readMeasure(measure, state));
  return { reads, state };
}

/** What every part says about the key at one bar, in score order. */
function keyStatements(parts: readonly PartRead[], index: number): readonly KeyStatement[] {
  return parts.map((part) => ({
    key: part.reads[index]?.key ?? null,
    // The bar's own transposition, not the part's: the signature a bar prints is read
    // against whatever instrument is in the player's hands at it.
    transposition: part.reads[index]?.transposition ?? openingTransposition(part),
  }));
}

/** What a part read at before it picked anything else up. */
function openingTransposition(read: PartRead): Transposition {
  return read.state.opened?.transposition ?? AT_PITCH;
}

/** The first statement any part makes about a bar, or `null` when none of them does. */
function stated<T>(
  parts: readonly PartRead[],
  index: number,
  pick: (read: MeasureRead) => T | null,
): T | null {
  for (const part of parts) {
    const read = part.reads[index];
    if (read === undefined) continue;
    const value = pick(read);
    if (value !== null) return value;
  }
  return null;
}

function anyOf(
  parts: readonly PartRead[],
  index: number,
  pick: (read: MeasureRead) => boolean,
): boolean {
  return parts.some((part) => {
    const read = part.reads[index];
    return read !== undefined && pick(read);
  });
}

/**
 * The length of a bar that is shorter than its metre, or `null` for an ordinary one.
 *
 * Only a bar the file itself calls `implicit` is shortened. Reading a short bar out of the
 * notes instead would shorten a bar whenever one part was written with a missing rest,
 * which is a common fault in hand-written files and would move every bar after it.
 */
function statedLength(parts: readonly PartRead[], index: number, metre: number): number | null {
  if (!anyOf(parts, index, (read) => read.implicit)) return null;
  let filled = 0;
  for (const part of parts) {
    const read = part.reads[index];
    if (read !== undefined) filled = Math.max(filled, read.filled);
  }
  return filled > 0 && filled < metre ? filled : null;
}

/**
 * What a bar says about jumping, as any of its parts states it.
 *
 * A jump belongs to the piece, but MusicXML lets any part carry it and hand-made files put
 * it wherever the engraver happened to be working, so every part is read and what they say
 * is added together. A bar that says nothing holds `null` rather than five falsehoods.
 */
function jumpsOf(parts: readonly PartRead[], index: number): JumpMarks | null {
  const marks: JumpMarks = {
    segno: anyOf(parts, index, (read) => read.jumps.segno),
    coda: anyOf(parts, index, (read) => read.jumps.coda),
    toCoda: anyOf(parts, index, (read) => read.jumps.toCoda),
    fine: anyOf(parts, index, (read) => read.jumps.fine),
    jump: stated(parts, index, (read) => read.jumps.jump),
  };
  const bare = !marks.segno && !marks.coda && !marks.toCoda && !marks.fine && marks.jump === null;
  return bare ? null : marks;
}

/** Rebuild the piece's own timeline from what the parts say about each bar. */
function globalTimeline(parts: readonly PartRead[], count: number): readonly GlobalMeasure[] {
  const measures: GlobalMeasure[] = [];
  let endings: readonly number[] = [];
  let metre = measureTicks(COMMON_TIME.beats, COMMON_TIME.beatType);

  for (let index = 0; index < count; index += 1) {
    const start = anyOf(parts, index, (read) => read.repeatStart);
    const end = anyOf(parts, index, (read) => read.repeatEnd);

    // An ending the file never stopped ends where the next repeated section begins: a bar
    // behind a forward repeat is the start of new music, not more of a volta. Carried on, the
    // bracket would take in every bar to the next one it stops, and those bars would play on
    // one pass of the section before it and not at all on the other.
    if (start) endings = [];
    const opened = stated(parts, index, (read) => read.endingStart);
    if (opened !== null) endings = opened;
    const times = end ? stated(parts, index, (read) => read.repeatTimes) : null;
    const repeat: RepeatMarks | null =
      start || end || endings.length > 0 ? { start, end, endings, times } : null;

    const jump = jumpsOf(parts, index);

    const time = stated(parts, index, (read) => read.time) ?? (index === 0 ? COMMON_TIME : null);
    if (time !== null) metre = measureTicks(time.beats, time.beatType);

    measures.push({
      id: createId("measure"),
      time,
      key: pieceKey(keyStatements(parts, index)) ?? (index === 0 ? C_MAJOR : null),
      tempo: stated(parts, index, (read) => read.tempo),
      barline: barlineStyleOf(stated(parts, index, (read) => read.barline)),
      repeat,
      jump,
      rehearsal: stated(parts, index, (read) => read.rehearsal),
      breakBefore: stated(parts, index, (read) => read.breakBefore),
      length: statedLength(parts, index, metre),
    });

    if (anyOf(parts, index, (read) => read.endingStop)) endings = [];
  }

  return measures;
}

/**
 * How many staves the file drew for a part, counting the ones its notes landed on and any
 * ossia staff among them. These are the file's staves, not yet the document's.
 */
function staffCountOf(read: PartRead): number {
  let count = read.state.staffCount;
  for (const measure of read.reads) {
    for (const voice of measure.measure.voices) count = Math.max(count, voice.staff + 1);
  }
  for (const staff of read.state.ossiaStaves) count = Math.max(count, staff + 1);
  return Math.max(1, count);
}

/** The file's staves that are the part's own, in order, dropping the ossia staves. */
function ownStaves(map: OssiaMap): readonly number[] {
  const own: number[] = [];
  map.ossia.forEach((ossia, at) => {
    if (!ossia) own.push(at);
  });
  return own.length === 0 ? [0] : own;
}

/**
 * The part's lines, with any duplicate dropped.
 *
 * A chord is several `<note>` elements in the file and one event here, so a glissando drawn
 * on each note of a chord arrives as several lines between the same pair of events. They
 * say one thing -- this chord slides to that one -- and keeping all of them would write the
 * gesture out several times over, once for every note of the chord it was read from.
 */
function spannersOf(read: PartRead): readonly Spanner[] {
  const seen = new Set<string>();
  const spanners: Spanner[] = [];
  for (const span of read.state.closed) {
    const key = `${span.kind}:${span.from}:${span.to}:${span.placement}`;
    if (seen.has(key)) continue;
    seen.add(key);
    spanners.push({
      id: createId("spanner"),
      kind: span.kind,
      from: span.from,
      to: span.to,
      placement: span.placement,
    });
  }
  return spanners;
}

function buildPart(entry: PartEntry, read: PartRead, timeline: readonly GlobalMeasure[]): Part {
  const ids: readonly MeasureId[] = timeline.map((measure) => measure.id);
  // The file's staves first, because an ossia is one of them there and none of them here.
  const map = ossiaMap(read.state.ossiaStaves, staffCountOf(read));
  const plain = isPlain(map);
  const staves: Staff[] = ownStaves(map).map((at) => ({
    id: createId("staff"),
    clef: read.reads[0]?.clefs[at] ?? TREBLE,
    lines: read.state.staffLines.get(at) ?? 5,
  }));

  const measures: Record<MeasureId, PartMeasure> = {};
  ids.forEach((id, index) => {
    const bar = read.reads[index];
    if (bar === undefined) {
      measures[id] = emptyPartMeasure(map.count);
      return;
    }
    // The opening clefs became the staves; only later bars hold changes.
    const measure = index === 0 ? { ...bar.measure, clefChanges: {} } : bar.measure;
    measures[id] = plain ? measure : remapMeasure(measure, map);
  });

  return {
    id: createId("part"),
    name: entry.name,
    abbreviation: entry.abbreviation,
    instrument: {
      ...entry.instrument,
      // What the part opened on. Taking the last `<transpose>` the file states instead
      // would give a doubling part the instrument it ends the piece holding.
      transposition: openingTransposition(read),
      percussion: kitFrom(entry.instrument.name, entry.drums, read.state.seen),
      tuning: read.state.tuning,
      keyPrinting: keyPrintingOf(
        read.reads.map((bar) => ({ key: bar.key ?? null, transposition: bar.transposition })),
        timeline,
      ),
    },
    staves,
    measures,
    spanners: spannersOf(read),
  };
}

/** Read a MusicXML document. */
export function fromMusicXml(source: string): ImportResult {
  let root: XmlElement;
  try {
    root = parseXml(source);
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : "Could not parse XML" };
  }

  if (root.name === "score-timewise") {
    return { ok: false, reason: "Timewise MusicXML is not supported; convert it to partwise." };
  }
  if (root.name !== "score-partwise") {
    return { ok: false, reason: `Not a MusicXML score: the root element is <${root.name}>.` };
  }

  const { parts: entries, groups } = readPartList(root);
  const elements = childrenNamed(root, "part");
  const warnings: string[] = [];

  const reads = entries.map((entry, index) => {
    const matched =
      elements.find((element) => attr(element, "id") === entry.xmlId) ?? elements[index] ?? null;
    if (matched === null) warnings.push(`Part "${entry.name}" has no music in the file.`);
    return readPart(matched, entry);
  });

  const count = Math.max(1, ...reads.map((read) => read.reads.length));
  const measures = globalTimeline(reads, count);
  const parts = entries.map((entry, index) =>
    buildPart(entry, reads[index] ?? readPart(null, entry), measures),
  );
  const partIds: readonly PartId[] = parts.map((part) => part.id);

  for (const read of reads) {
    if (read.state.open.size > 0) warnings.push("A slur, hairpin or other line was never closed.");
  }

  const score: Score = {
    id: createId("score"),
    meta: readMeta(root),
    // A file that gathers any part's rests is a part, and is opened as one.
    layout: readLayout(
      root,
      reads.some((read) => read.state.gathered),
    ),
    measures,
    parts,
    groups: groups.map((group): StaffGroup => groupOf(group, partIds)),
  };
  return { ok: true, score: simplifyAccidentals(score), warnings };
}
